import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, RefreshCw, Undo2 } from "lucide-react";
import { planLabel } from "@/lib/subscription";
import { toast } from "sonner";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const BillingHealthPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [winback, setWinback] = useState(null);
  const [sendingWinback, setSendingWinback] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/billing-health");
      setData(r.data);
      const w = await api.get("/admin/winback/preview");
      setWinback(w.data);
    } catch {
      toast.error("Could not load billing health");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendWinback = async () => {
    if (!window.confirm(`Send the one-time "welcome back" email to ${winback?.eligible} expired member${winback?.eligible === 1 ? "" : "s"}?\n\nEach person is only ever emailed once.`)) return;
    setSendingWinback(true);
    try {
      const r = await api.post("/admin/winback/send");
      toast.success(`Welcome-back email sent to ${r.data.sent} of ${r.data.eligible} expired members`);
      const w = await api.get("/admin/winback/preview");
      setWinback(w.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Win-back send failed");
    } finally {
      setSendingWinback(false);
    }
  };

  return (
    <div className="obs-card" data-testid="billing-health-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <AlertTriangle className={`h-5 w-5 ${data?.count ? "text-red-400" : "text-blaze"}`} />
          <div>
            <div className="overline">Billing health</div>
            <h3 className="font-heading text-2xl uppercase">
              {data === null ? "…" : data.count === 0 ? "All cards charging" : `${data.count} past due`}
            </h3>
          </div>
        </div>
        <button
          type="button"
          data-testid="billing-health-refresh"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-obsidian-600 px-3 py-2 rounded-sm text-xs uppercase tracking-wider text-zinc-300 hover:border-blaze hover:text-blaze disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {data?.count === 0 && (
        <p className="text-sm text-zinc-500" data-testid="billing-health-empty">
          No failing renewals — every active subscription charged successfully.
        </p>
      )}

      {(data?.past_due || []).length > 0 && (
        <div className="overflow-x-auto">
          <p className="text-xs text-zinc-500 mb-3">
            These members' renewal charges are failing. Their access is paused and we retry
            automatically every 10 minutes — chase them to update their card on the Pricing page.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="py-2 pr-4 font-medium">Member</th>
                <th className="py-2 pr-4 font-medium">Plan</th>
                <th className="py-2 pr-4 font-medium">Amount</th>
                <th className="py-2 pr-4 font-medium">Paid through</th>
                <th className="py-2 pr-4 font-medium">Fails</th>
                <th className="py-2 font-medium">Why</th>
              </tr>
            </thead>
            <tbody>
              {data.past_due.map((r) => (
                <tr key={r.user_id} className="border-t border-obsidian-600" data-testid={`billing-health-row-${r.user_id}`}>
                  <td className="py-2.5 pr-4">
                    <div className="text-zinc-200">{r.first_name || "—"}</div>
                    <div className="text-[11px] text-zinc-500">{r.email}</div>
                  </td>
                  <td className="py-2.5 pr-4 text-zinc-300">{planLabel(r.plan)}</td>
                  <td className="py-2.5 pr-4 font-data text-zinc-300">{money(r.amount_cents)}</td>
                  <td className="py-2.5 pr-4 text-zinc-400">
                    {r.paid_through ? new Date(r.paid_through).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2.5 pr-4 font-data text-red-400">{r.renewal_failures}</td>
                  <td className="py-2.5 text-xs text-zinc-400">
                    {r.reason || "—"}{!r.has_card && " (no card on file)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {winback !== null && (winback.eligible > 0 || winback.already_sent > 0) && (
        <div className="mt-4 pt-4 border-t border-obsidian-600 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="winback-section">
          <p className="text-xs text-zinc-500 flex-1">
            <span className="text-zinc-300">{winback.eligible}</span> expired member{winback.eligible === 1 ? "" : "s"} haven't
            received the one-time "welcome back" re-subscribe email
            {winback.already_sent > 0 && ` (${winback.already_sent} already emailed)`}.
          </p>
          {winback.eligible > 0 && (
            <button
              type="button"
              data-testid="winback-send-btn"
              onClick={sendWinback}
              disabled={sendingWinback}
              className="shrink-0 inline-flex items-center gap-2 border border-obsidian-600 px-3 py-2 rounded-sm text-xs uppercase tracking-wider text-zinc-300 hover:border-blaze hover:text-blaze disabled:opacity-50"
            >
              <Undo2 className="h-3.5 w-3.5" /> {sendingWinback ? "Sending…" : "Send welcome-back email"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingHealthPanel;
